import React, { useState } from 'react';
import { 
  FileText, 
  Layers, 
  Database, 
  ShieldCheck, 
  Sparkles, 
  Globe, 
  Palette, 
  Layout, 
  CheckCircle2, 
  ChevronRight,
  Code,
  Search,
  Zap,
  Lock
} from 'lucide-react';
import { SectionId, SectionMeta } from '../../types';
import { 
  SECTIONS_META, 
  MARKET_WAVES, 
  DATABASE_TABLES, 
  SYSTEM_METRICS 
} from '../../data/architectureData';
import { DESIGN_SYSTEM_TOKENS } from '../../data/designTokens';
import { DELIVERABLES_P6 } from '../../data/salaryTaxPacksData';
import { DELIVERABLES_P7 } from '../../data/loanCountryPacksData';
import { DELIVERABLES_P8 } from '../../data/identityData';
import { DELIVERABLES_P9 } from '../../data/seoCmsData';
import { DELIVERABLES_P10 } from '../../data/monetizationData';
import { DELIVERABLES_P11 } from '../../data/analyticsData';

export const ArchitectureView: React.FC = () => {
  const [selectedSection, setSelectedSection] = useState<SectionId>('ux-spec-p2');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredSections = SECTIONS_META.filter(
    (s) =>
      s.titleEn.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.titleFr.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.summary.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8 pb-20">
      {/* Header */}
      <div className="border-b border-[#E5E7EB] pb-6 space-y-2">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#1A1A1A] text-white rounded-full text-xs font-bold">
          <FileText className="w-3.5 h-3.5" />
          <span>YamooPay Master Technical Specification & Blueprint</span>
        </div>
        <h1 className="text-3xl font-extrabold text-[#1A1A1A] tracking-tight">
          Executive Architecture & Design System (Prompt 01 + 02)
        </h1>
        <p className="text-sm text-[#6B7280] max-w-3xl">
          Comprehensive technical deliverables (A → AE) covering global UX vision, information architecture, light design system tokens, database schemas, internationalization, and QA criteria.
        </p>
      </div>

      {/* Main Grid: Sidebar + Content */}
      <div className="grid lg:grid-cols-12 gap-8 items-start">
        {/* Left Sidebar Menu */}
        <div className="lg:col-span-4 bg-white border border-[#E5E7EB] rounded-2xl p-4 space-y-3 shadow-xs">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Filter specifications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 bg-[#FBFBFC] border border-[#E5E7EB] rounded-xl text-xs font-semibold text-[#1A1A1A] outline-none"
            />
          </div>

          <div className="space-y-1 max-h-[650px] overflow-y-auto pr-1">
            {/* Prompt 10 Specific Pin */}
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 px-2 pt-2">
              Prompt 10: Monetization & Revenue Intelligence
            </div>

            <button
              onClick={() => setSelectedSection('monetization-p10' as any)}
              className={`w-full text-left p-2.5 rounded-xl text-xs transition-all flex items-center justify-between cursor-pointer ${
                selectedSection === ('monetization-p10' as any)
                  ? 'bg-emerald-700 text-white font-bold'
                  : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-900 font-semibold border border-emerald-200'
              }`}
            >
              <div className="flex items-center gap-2">
                <Zap className="w-3.5 h-3.5 text-emerald-600" />
                <span>Monetization & Revenue Engine (DEL-P10)</span>
              </div>
              <ChevronRight className="w-3 h-3 opacity-80" />
            </button>

            {/* Prompt 09 Specific Pin */}
            <div className="text-[10px] font-bold uppercase tracking-wider text-purple-700 px-2 pt-2">
              Prompt 09: Global SEO, Programmatic & Content CMS
            </div>

            <button
              onClick={() => setSelectedSection('seo-growth-p9' as any)}
              className={`w-full text-left p-2.5 rounded-xl text-xs transition-all flex items-center justify-between cursor-pointer ${
                selectedSection === ('seo-growth-p9' as any)
                  ? 'bg-purple-700 text-white font-bold'
                  : 'bg-purple-50 hover:bg-purple-100 text-purple-800 font-semibold border border-purple-200'
              }`}
            >
              <div className="flex items-center gap-2">
                <Search className="w-3.5 h-3.5" />
                <span>SEO, Programmatic & CMS (DEL-P9)</span>
              </div>
              <ChevronRight className="w-3 h-3 opacity-80" />
            </button>

            {/* Prompt 06 Specific Pin */}
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#0E7044] px-2 pt-2">
              Prompt 06: Salary, Paycheck & Tax Engine
            </div>

            <button
              onClick={() => setSelectedSection('salary-engine-p6' as any)}
              className={`w-full text-left p-2.5 rounded-xl text-xs transition-all flex items-center justify-between cursor-pointer ${
                selectedSection === ('salary-engine-p6' as any)
                  ? 'bg-[#0E7044] text-white font-bold'
                  : 'bg-[#E8F5E9] hover:bg-[#C8E6C9] text-[#0E7044] font-semibold border border-[#C8E6C9]'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText className="w-3.5 h-3.5" />
                <span>Salary & Tax Architecture (DEL-P6)</span>
              </div>
              <ChevronRight className="w-3 h-3 opacity-80" />
            </button>

            {/* Prompt 03 Specific Pins */}
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#1E40AF] px-2 pt-2">
              Prompt 03: PostgreSQL & Data Governance
            </div>

            <button
              onClick={() => setSelectedSection('data-governance-p3' as any)}
              className={`w-full text-left p-2.5 rounded-xl text-xs transition-all flex items-center justify-between cursor-pointer ${
                selectedSection === ('data-governance-p3' as any)
                  ? 'bg-[#1E40AF] text-white font-bold'
                  : 'bg-[#F0F4FF] hover:bg-[#E0EAFF] text-[#1E40AF] font-semibold border border-[#C7D7FE]'
              }`}
            >
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5" />
                <span>Financial Data Trust Layer</span>
              </div>
              <ChevronRight className="w-3 h-3 opacity-80" />
            </button>

            {/* Prompt 02 Specific Pins */}
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#9CA3AF] px-2 pt-2">
              Prompt 02 Deliverables (Global UX & Design System)
            </div>

            <button
              onClick={() => setSelectedSection('ux-spec-p2' as any)}
              className={`w-full text-left p-2.5 rounded-xl text-xs transition-all flex items-center justify-between ${
                selectedSection === ('ux-spec-p2' as any)
                  ? 'bg-[#1A1A1A] text-white font-bold'
                  : 'hover:bg-[#F3F4F6] text-[#1A1A1A]'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] opacity-70">A-M</span>
                <span>UX Vision & Wireframes</span>
              </div>
              <ChevronRight className="w-3 h-3 opacity-60" />
            </button>

            <button
              onClick={() => setSelectedSection('tokens-p2' as any)}
              className={`w-full text-left p-2.5 rounded-xl text-xs transition-all flex items-center justify-between ${
                selectedSection === ('tokens-p2' as any)
                  ? 'bg-[#1A1A1A] text-white font-bold'
                  : 'hover:bg-[#F3F4F6] text-[#1A1A1A]'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] opacity-70">N-Z</span>
                <span>Design System Tokens & Specs</span>
              </div>
              <ChevronRight className="w-3 h-3 opacity-60" />
            </button>

            <div className="text-[10px] font-bold uppercase tracking-wider text-[#9CA3AF] px-2 pt-3">
              Prompt 01 Architecture Modules
            </div>

            {filteredSections.map((sec) => (
              <button
                key={sec.id}
                onClick={() => setSelectedSection(sec.id)}
                className={`w-full text-left p-2.5 rounded-xl text-xs transition-all flex items-center justify-between ${
                  selectedSection === sec.id
                    ? 'bg-[#1A1A1A] text-white font-bold'
                    : 'hover:bg-[#F3F4F6] text-[#1A1A1A]'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] opacity-70">{sec.letter}</span>
                  <span className="truncate">{sec.titleEn}</span>
                </div>
                <ChevronRight className="w-3 h-3 opacity-60" />
              </button>
            ))}
          </div>
        </div>

        {/* Right Detail Content Panel */}
        <div className="lg:col-span-8 bg-white border border-[#E5E7EB] rounded-2xl p-6 sm:p-8 space-y-6 shadow-xs min-h-[600px]">
          {/* PROMPT 06 SALARY & TAX ARCHITECTURE SPECIFICATIONS */}
          {selectedSection === ('salary-engine-p6' as any) && (
            <div className="space-y-6 animate-in fade-in duration-150">
              <div className="border-b border-[#E5E7EB] pb-4 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#E8F5E9] text-[#0E7044] border border-[#C8E6C9]">
                    Prompt 06 Deliverables (DEL-P6-A → F)
                  </span>
                  <span className="text-xs text-[#6B7280]">Global Salary, Paycheck & Income Tax Engine</span>
                </div>
                <h2 className="text-2xl font-extrabold text-[#1A1A1A]">
                  Global Salary, Paycheck, Income Tax & Compensation Intelligence
                </h2>
                <p className="text-xs text-[#6B7280]">
                  Deterministic gross-to-net pipeline, binary search net-to-gross solver, total employer cost of employment, 2026 versioned tax rulesets, and explainability traces.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                {DELIVERABLES_P6.map((del) => (
                  <div key={del.code} className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-1.5 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px] font-bold text-[#0E7044] bg-[#E8F5E9] px-1.5 py-0.5 rounded">
                        {del.code}
                      </span>
                      <span className="text-[10px] text-[#6B7280]">{del.category}</span>
                    </div>
                    <div className="font-bold text-[#1A1A1A] text-sm">{del.titleEn}</div>
                    <p className="text-[#6B7280] leading-relaxed">{del.summary}</p>
                    <ul className="list-disc pl-4 text-[11px] text-[#555555] space-y-0.5 pt-1">
                      {del.details.slice(0, 3).map((d, i) => (
                        <li key={i}>{d}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* PROMPT 10 SPECIFIC DELIVERABLES VIEW */}
          {selectedSection === ('monetization-p10' as any) && (
            <div className="space-y-6 animate-in fade-in duration-150">
              <div className="border-b border-emerald-200 pb-4 space-y-2">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded bg-emerald-100 text-emerald-900 text-xs font-bold">
                  <Zap className="w-3.5 h-3.5 text-emerald-700" />
                  PROMPT 10 CERTIFIED SPECIFICATIONS
                </div>
                <h2 className="text-2xl font-extrabold text-[#1A1A1A]">
                  YamooPay Global Monetization & Revenue Intelligence Engine
                </h2>
                <p className="text-xs text-[#6B7280]">
                  6 Certified Deliverables: Commercial Governance & Ranking Firewall, AdSlot Abstraction, Protected Calculation Zones, TCF/GPP Privacy Signals, ads.txt Security, and Monetization Command Center.
                </p>
              </div>

              <div className="space-y-4">
                {DELIVERABLES_P10.map((del) => (
                  <div key={del.code} className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold bg-emerald-700 text-white px-2 py-0.5 rounded">
                          {del.code}
                        </span>
                        <h3 className="font-bold text-sm text-[#1A1A1A]">{del.titleEn}</h3>
                      </div>
                      <span className="text-[10px] font-bold bg-white px-2 py-0.5 rounded border border-[#E5E7EB] text-emerald-800">
                        {del.category}
                      </span>
                    </div>

                    <p className="text-xs text-[#4B5563] font-medium">{del.summary}</p>

                    <div className="p-3 bg-white rounded-lg border border-[#E5E7EB] space-y-1.5">
                      <div className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider">Key Functional Requirements</div>
                      <ul className="space-y-1 text-xs text-[#4B5563]">
                        {del.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-emerald-700 font-bold">•</span>
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="text-[11px] font-mono text-[#6B7280] bg-[#F3F4F6] p-2 rounded border border-[#E5E7EB]">
                      <span className="font-bold text-[#1A1A1A]">Architecture Note:</span> {del.architectureNotes}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 0. PROMPT 03 DATA GOVERNANCE SPECIFICATIONS */}
          {selectedSection === ('data-governance-p3' as any) && (
            <div className="space-y-6 animate-in fade-in duration-150">
              <div className="border-b border-[#E5E7EB] pb-4 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#F0F4FF] text-[#1E40AF] border border-[#C7D7FE]">
                    Prompt 03 Deliverables (A → AT)
                  </span>
                  <span className="text-xs text-[#6B7280]">46 Complete Architectural Modules</span>
                </div>
                <h2 className="text-2xl font-extrabold text-[#1A1A1A]">
                  PostgreSQL 18 Data Governance, Quality & Freshness Engine
                </h2>
                <p className="text-xs text-[#6B7280]">
                  Single System of Record for sovereign geography, currencies, bitemporal tax rulesets, and 7-dimension automated data quality.
                </p>
              </div>

              {/* Core Pillars Grid */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-1.5">
                  <div className="text-xs font-bold text-[#1A1A1A] flex items-center gap-1.5">
                    <Database className="w-4 h-4 text-[#1E40AF]" />
                    <span>PostgreSQL 18 System of Record</span>
                  </div>
                  <p className="text-xs text-[#6B7280] leading-relaxed">
                    Zero float arithmetic (<code className="text-[#1A1A1A] font-semibold">NUMERIC(18,4)</code>), UUIDv7 primary keys, and partitioned tables for high-frequency FX tick series.
                  </p>
                </div>

                <div className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-1.5">
                  <div className="text-xs font-bold text-[#1A1A1A] flex items-center gap-1.5">
                    <ShieldCheck className="w-4 h-4 text-[#0E7044]" />
                    <span>7-Dimension Quality Engine</span>
                  </div>
                  <p className="text-xs text-[#6B7280] leading-relaxed">
                    Automated pre-promotion validation checking Completeness, Validity, Uniqueness, Consistency, Timeliness, Accuracy, and Referential Integrity.
                  </p>
                </div>

                <div className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-1.5">
                  <div className="text-xs font-bold text-[#1A1A1A] flex items-center gap-1.5">
                    <Lock className="w-4 h-4 text-[#B42318]" />
                    <span>Bitemporal & Four-Eyes Governance</span>
                  </div>
                  <p className="text-xs text-[#6B7280] leading-relaxed">
                    Maker/Checker dual-authorization workflow for statutory tax bracket updates with immutable audit trails.
                  </p>
                </div>

                <div className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-1.5">
                  <div className="text-xs font-bold text-[#1A1A1A] flex items-center gap-1.5">
                    <Zap className="w-4 h-4 text-amber-700" />
                    <span>Freshness Engine & LKG Fallback</span>
                  </div>
                  <p className="text-xs text-[#6B7280] leading-relaxed">
                    Strict domain TTL policies with automated Last Known Good failover and public UI trust provenance indicators.
                  </p>
                </div>
              </div>

              <div className="p-4 bg-[#F0F4FF] rounded-xl border border-[#C7D7FE] flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
                <div>
                  <div className="font-bold text-[#1E40AF]">Live Governance Command Center Active</div>
                  <div className="text-[#1E3A8A] text-[11px]">Inspect real-time schemas, triage quarantine items, and explore source health telemetry.</div>
                </div>
                <button
                  onClick={() => {
                    let event;
                    try {
                      event = new CustomEvent('navigate-governance');
                    } catch (e) {
                      event = document.createEvent('CustomEvent');
                      (event as any).initCustomEvent('navigate-governance', false, false, null);
                    }
                    window.dispatchEvent(event);
                  }}
                  className="px-3.5 py-1.5 bg-[#1E40AF] text-white rounded-lg font-bold hover:bg-[#1E3A8A] transition-colors cursor-pointer text-center shrink-0"
                >
                  Open Command Center
                </button>
              </div>
            </div>
          )}

          {/* 1. PROMPT 02 UX SPECIFICATIONS (A - M) */}
          {selectedSection === ('ux-spec-p2' as any) && (
            <div className="space-y-6 animate-in fade-in duration-150">
              <div className="border-b border-[#E5E7EB] pb-4 space-y-1">
                <div className="text-xs font-bold text-[#6B7280] uppercase tracking-wider">
                  Deliverables A → M
                </div>
                <h2 className="text-2xl font-extrabold text-[#1A1A1A]">
                  Global UX Vision, Information Architecture & Responsive Wireframes
                </h2>
                <p className="text-xs text-[#6B7280]">
                  Grounded in "Calm Finance", Light Mode Only, 100% Client-Side Privacy, and Global-First Neutrality.
                </p>
              </div>

              {/* Core Tenet Grid */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-2">
                  <div className="text-xs font-bold text-[#1A1A1A] flex items-center gap-1.5">
                    <ShieldCheck className="w-4 h-4 text-emerald-700" />
                    <span>Clarity Over Decoration</span>
                  </div>
                  <p className="text-xs text-[#6B7280] leading-relaxed">
                    Zero visual clutter, no dark gradients, no aggressive neon cards. Pure typography, high mathematical contrast, and instant tabular results.
                  </p>
                </div>

                <div className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-2">
                  <Globe className="w-4 h-4 text-[#1A1A1A]" />
                  <span>Global-First Neutrality</span>
                  <p className="text-xs text-[#6B7280] leading-relaxed">
                    Default base currencies (USD, EUR, GBP) without regional bias. 15+ dedicated country tax packs with instant switcher.
                  </p>
                </div>

                <div className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-2">
                  <Zap className="w-4 h-4 text-[#1A1A1A]" />
                  <span>Progressive Disclosure</span>
                  <p className="text-xs text-[#6B7280] leading-relaxed">
                    Simple primary fields on load. Advanced tax deductions, prepayment penalties, and custom fee inputs expand smoothly on demand.
                  </p>
                </div>

                <div className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-2">
                  <Lock className="w-4 h-4 text-[#1A1A1A]" />
                  <span>Client-Side Execution</span>
                  <p className="text-xs text-[#6B7280] leading-relaxed">
                    Financial numbers stay private on the user device. Calculations work offline with instantaneous responsiveness.
                  </p>
                </div>
              </div>

              {/* Homepage Canonical 11-Step Structure */}
              <div className="space-y-3 pt-4 border-t border-[#E5E7EB]">
                <h3 className="text-sm font-bold text-[#1A1A1A]">
                  Canonical 11-Step Homepage Structure
                </h3>
                <div className="space-y-2">
                  {[
                    { step: '01', title: 'Global Header', desc: 'Logo + Mega Menu (Convert, Earn, Send, Borrow, Grow, Everyday) + Smart Search + Region Selector + Workspace' },
                    { step: '02', title: 'Location Detection & Hero', desc: 'Discreet location notice + "Smarter money calculations. Wherever you live." + Smart Search Command trigger' },
                    { step: '03', title: 'Quick Tools Interactive Playground', desc: 'Live instant calculators: Currency, Salary & Paycheck, Compare Transfers, Mortgage' },
                    { step: '04', title: 'Popular Right Now', desc: 'Live wholesale FX tickers and volume reserve pairs' },
                    { step: '05', title: 'Tools for Your Region', desc: 'Jurisdiction-calibrated tools for active user locale' },
                    { step: '06', title: 'Explore Financial Tools', desc: 'Categorized directory with direct launches' },
                    { step: '07', title: 'Radical Transparency & Trust', desc: 'Data freshness indicators, open mathematical methodology drawer' },
                    { step: '08', title: 'Global Reach Metrics', desc: '170+ Currencies, 50+ Tax Engines, 120+ Corridors, 100% Client-Side' },
                    { step: '09', title: 'Learn About Your Money', desc: 'Educational guides (Hidden FX spreads, Marginal vs Effective Tax)' },
                    { step: '10', title: 'Final Value Call-to-Action', desc: 'Clean, pressure-free tool exploration prompt' },
                    { step: '11', title: 'Comprehensive Global Footer', desc: 'Currency indices, tools catalog, methodology disclosures, accessibility' },
                  ].map((s) => (
                    <div key={s.step} className="p-3 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] flex items-start gap-3 text-xs">
                      <span className="font-mono font-bold text-[#1A1A1A] bg-white px-2 py-0.5 rounded border border-[#E5E7EB]">
                        {s.step}
                      </span>
                      <div>
                        <div className="font-bold text-[#1A1A1A]">{s.title}</div>
                        <div className="text-[#6B7280] mt-0.5">{s.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* 2. PROMPT 02 DESIGN SYSTEM TOKENS (N - Z) */}
          {selectedSection === ('tokens-p2' as any) && (
            <div className="space-y-6 animate-in fade-in duration-150">
              <div className="border-b border-[#E5E7EB] pb-4 space-y-1">
                <div className="text-xs font-bold text-[#6B7280] uppercase tracking-wider">
                  Deliverables N → Z
                </div>
                <h2 className="text-2xl font-extrabold text-[#1A1A1A]">
                  Design System Tokens, Typography & Component Standards
                </h2>
                <p className="text-xs text-[#6B7280]">
                  Strict Light Mode token dictionary and component library specifications.
                </p>
              </div>

              {DESIGN_SYSTEM_TOKENS.map((cat, idx) => (
                <div key={idx} className="space-y-3">
                  <div className="space-y-0.5">
                    <h3 className="text-sm font-bold text-[#1A1A1A]">{cat.name}</h3>
                    <p className="text-xs text-[#6B7280]">{cat.description}</p>
                  </div>

                  <div className="border border-[#E5E7EB] rounded-xl overflow-hidden">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-[#F3F4F6] border-b border-[#E5E7EB] text-[#1A1A1A]">
                        <tr>
                          <th className="p-2.5 font-bold">Token Name</th>
                          <th className="p-2.5 font-bold">Value / Spec</th>
                          <th className="p-2.5 font-bold">Intended Usage</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5E7EB]">
                        {cat.tokens.map((tok, tIdx) => (
                          <tr key={tIdx} className="hover:bg-[#FBFBFC]">
                            <td className="p-2.5 font-mono font-bold text-[#1A1A1A] flex items-center gap-2">
                              {tok.hexOrCss && (
                                <span
                                  className="w-3.5 h-3.5 rounded-full border border-[#E5E7EB] shrink-0"
                                  style={{ backgroundColor: tok.hexOrCss }}
                                />
                              )}
                              <span>{tok.name}</span>
                            </td>
                            <td className="p-2.5 font-mono text-[#6B7280]">{tok.value}</td>
                            <td className="p-2.5 text-[#6B7280]">{tok.usage}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 3. PROMPT 01 DATA MODEL */}
          {selectedSection === 'data-model' && (
            <div className="space-y-6 animate-in fade-in duration-150">
              <div className="border-b border-[#E5E7EB] pb-4 space-y-1">
                <div className="text-xs font-bold text-[#6B7280] uppercase tracking-wider">
                  Relational PostgreSQL Schema
                </div>
                <h2 className="text-2xl font-extrabold text-[#1A1A1A]">
                  Institutional Data Architecture ({DATABASE_TABLES.length} Core Tables)
                </h2>
                <p className="text-xs text-[#6B7280]">
                  Normalized relational schemas for multi-jurisdiction tax brackets, institutional FX tick history, and user calculation bookmarks.
                </p>
              </div>

              <div className="space-y-4">
                {DATABASE_TABLES.map((table) => (
                  <div key={table.name} className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Database className="w-4 h-4 text-[#1A1A1A]" />
                        <span className="font-mono font-bold text-sm text-[#1A1A1A]">{table.name}</span>
                      </div>
                      <span className="text-[10px] font-bold bg-white px-2 py-0.5 rounded border border-[#E5E7EB] text-[#6B7280]">
                        {table.category}
                      </span>
                    </div>
                    <p className="text-xs text-[#6B7280]">{table.description}</p>

                    <div className="border border-[#E5E7EB] rounded-lg overflow-hidden bg-white">
                      <table className="w-full text-left text-xs">
                        <thead className="bg-[#F3F4F6] text-[#1A1A1A]">
                          <tr>
                            <th className="p-2 font-bold">Column</th>
                            <th className="p-2 font-bold">Type</th>
                            <th className="p-2 font-bold">Constraints</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#E5E7EB]">
                          {table.columns.map((c) => (
                            <tr key={c.name}>
                              <td className="p-2 font-mono font-semibold text-[#1A1A1A]">{c.name}</td>
                              <td className="p-2 font-mono text-[#6B7280]">{c.type}</td>
                              <td className="p-2 text-[11px] text-[#6B7280]">
                                {c.isPrimary && <span className="text-amber-700 font-bold mr-1">PK</span>}
                                {c.isForeign && <span className="text-blue-700 font-bold mr-1">FK ({c.ref})</span>}
                                {!c.nullable ? 'NOT NULL' : 'NULL'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 4. MARKETS & REGIONAL EXPANSION */}
          {selectedSection === 'markets' && (
            <div className="space-y-6 animate-in fade-in duration-150">
              <div className="border-b border-[#E5E7EB] pb-4 space-y-1">
                <div className="text-xs font-bold text-[#6B7280] uppercase tracking-wider">
                  Global Market Wave Strategy
                </div>
                <h2 className="text-2xl font-extrabold text-[#1A1A1A]">
                  Multi-Tier Regional Rollout & Currency Support
                </h2>
                <p className="text-xs text-[#6B7280]">
                  Global expansion roadmap across 4 distinct strategic market waves.
                </p>
              </div>

              <div className="space-y-4">
                {MARKET_WAVES.map((wave) => (
                  <div key={wave.wave} className="p-5 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-bold text-[#1A1A1A]">
                        Wave {wave.wave}: {wave.name}
                      </h3>
                      <span className="text-[11px] font-bold bg-[#1A1A1A] text-white px-2.5 py-0.5 rounded-full">
                        {wave.priority}
                      </span>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 pt-2">
                      {wave.countries.map((c) => (
                        <div key={c.code} className="p-2.5 bg-white rounded-lg border border-[#E5E7EB] text-xs space-y-0.5">
                          <div className="font-bold text-[#1A1A1A] flex items-center justify-between">
                            <span>{c.flag} {c.name}</span>
                            <span className="font-mono text-[10px] text-[#6B7280]">{c.currency}</span>
                          </div>
                          <div className="text-[10px] text-[#6B7280]">Tax Complexity: {c.taxComplexity}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 5. PROMPT 06 SALARY ENGINE DELIVERABLES */}
          {selectedSection === 'salary-engine-p6' && (
            <div className="space-y-6 animate-in fade-in duration-150">
              <div className="border-b border-[#E5E7EB] pb-4 space-y-1">
                <div className="text-xs font-bold text-[#6B7280] uppercase tracking-wider">
                  Payroll & Gross-to-Net Intelligence (Prompt 06)
                </div>
                <h2 className="text-2xl font-extrabold text-[#1A1A1A]">
                  Certified Deliverables & System Invariants ({DELIVERABLES_P6.length} Modules)
                </h2>
                <p className="text-xs text-[#6B7280]">
                  Formal specification for gross-to-net calculations, reverse net-to-gross solver, versioned 2026 tax packs, employer costs, and auditability.
                </p>
              </div>

              <div className="space-y-4">
                {DELIVERABLES_P6.map((del) => (
                  <div key={del.code} className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold bg-[#1A1A1A] text-white px-2 py-0.5 rounded">
                          {del.code}
                        </span>
                        <h3 className="font-bold text-sm text-[#1A1A1A]">{del.titleEn}</h3>
                      </div>
                      <span className="text-[10px] font-bold bg-white px-2 py-0.5 rounded border border-[#E5E7EB] text-[#0E7044]">
                        {del.category}
                      </span>
                    </div>

                    <p className="text-xs text-[#4B5563] font-medium">{del.summary}</p>

                    <div className="p-3 bg-white rounded-lg border border-[#E5E7EB] space-y-1.5">
                      <div className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider">Key Functional Requirements</div>
                      <ul className="space-y-1 text-xs text-[#4B5563]">
                        {del.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-[#0E7044] font-bold">•</span>
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="text-[11px] font-mono text-[#6B7280] bg-[#F3F4F6] p-2 rounded border border-[#E5E7EB]">
                      <span className="font-bold text-[#1A1A1A]">Architecture Note:</span> {del.architectureNotes}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 6. PROMPT 07 LOAN ENGINE DELIVERABLES */}
          {selectedSection === 'loan-engine-p7' && (
            <div className="space-y-6 animate-in fade-in duration-150">
              <div className="border-b border-[#E5E7EB] pb-4 space-y-1">
                <div className="text-xs font-bold text-[#6B7280] uppercase tracking-wider">
                  Global Loans, Mortgages & Credit Intelligence (Prompt 07)
                </div>
                <h2 className="text-2xl font-extrabold text-[#1A1A1A]">
                  Certified Deliverables & Sovereign Amortization ({DELIVERABLES_P7.length} Core Engines)
                </h2>
                <p className="text-xs text-[#6B7280]">
                  Sovereign compounding rules, payment frequency conversion, APR/APRC/TAEG IRR solvers, extra payment acceleration, and country lending packs.
                </p>
              </div>

              <div className="space-y-4">
                {DELIVERABLES_P7.map((del) => (
                  <div key={del.code} className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold bg-[#10B981] text-white px-2 py-0.5 rounded">
                          {del.code}
                        </span>
                        <h3 className="font-bold text-sm text-[#1A1A1A]">{del.titleEn}</h3>
                      </div>
                      <span className="text-[10px] font-bold bg-white px-2 py-0.5 rounded border border-[#E5E7EB] text-[#0E7044]">
                        {del.category}
                      </span>
                    </div>

                    <p className="text-xs text-[#4B5563] font-medium">{del.summary}</p>

                    <div className="p-3 bg-white rounded-lg border border-[#E5E7EB] space-y-1.5">
                      <div className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider">Key Functional Requirements</div>
                      <ul className="space-y-1 text-xs text-[#4B5563]">
                        {del.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-[#10B981] font-bold">•</span>
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="text-[11px] font-mono text-[#6B7280] bg-[#F3F4F6] p-2 rounded border border-[#E5E7EB]">
                      <span className="font-bold text-[#1A1A1A]">Architecture Note:</span> {del.architectureNotes}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 7. PROMPT 08 IDENTITY & SECURITY DELIVERABLES */}
          {selectedSection === 'identity-security-p8' && (
            <div className="space-y-6 animate-in fade-in duration-150">
              <div className="border-b border-[#E5E7EB] pb-4 space-y-1">
                <div className="text-xs font-bold text-[#6B7280] uppercase tracking-wider">
                  YamooPay Global Identity, Security & Personal Workspace (Prompt 08)
                </div>
                <h2 className="text-2xl font-extrabold text-[#1A1A1A]">
                  Certified Deliverables & Security Modules ({DELIVERABLES_P8.length} Core Systems)
                </h2>
                <p className="text-xs text-[#6B7280]">
                  Passkeys WebAuthn, OIDC federated social login, server-controlled sessions, step-up sudo mode, My YamooPay scenarios, privacy center & internal RBAC.
                </p>
              </div>

              <div className="space-y-4">
                {DELIVERABLES_P8.map((del) => (
                  <div key={del.code} className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold bg-[#0E7044] text-white px-2 py-0.5 rounded">
                          {del.code}
                        </span>
                        <h3 className="font-bold text-sm text-[#1A1A1A]">{del.titleEn}</h3>
                      </div>
                      <span className="text-[10px] font-bold bg-white px-2 py-0.5 rounded border border-[#E5E7EB] text-[#0E7044]">
                        {del.category}
                      </span>
                    </div>

                    <p className="text-xs text-[#4B5563] font-medium">{del.summary}</p>

                    <div className="p-3 bg-white rounded-lg border border-[#E5E7EB] space-y-1.5">
                      <div className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider">Key Functional Requirements</div>
                      <ul className="space-y-1 text-xs text-[#4B5563]">
                        {del.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-[#0E7044] font-bold">•</span>
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="text-[11px] font-mono text-[#6B7280] bg-[#F3F4F6] p-2 rounded border border-[#E5E7EB]">
                      <span className="font-bold text-[#1A1A1A]">Architecture Note:</span> {del.architectureNotes}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* PROMPT 09 SPECIFIC DELIVERABLES VIEW */}
          {selectedSection === ('seo-growth-p9' as any) && (
            <div className="space-y-6 animate-in fade-in duration-150">
              <div className="border-b border-purple-200 pb-4 space-y-2">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded bg-purple-100 text-purple-900 text-xs font-bold">
                  <Search className="w-3.5 h-3.5 text-purple-700" />
                  PROMPT 09 CERTIFIED SPECIFICATIONS
                </div>
                <h2 className="text-2xl font-extrabold text-[#1A1A1A]">
                  YamooPay Global Organic Growth, Programmatic SEO & Financial CMS Engine
                </h2>
                <p className="text-xs text-[#6B7280]">
                  6 Certified Deliverables: E-E-A-T Editorial Governance, Financial Content CMS, Programmatic Quality Gate, Search Console Intelligence, Hreflang Graph, and Schema.org Registry.
                </p>
              </div>

              <div className="space-y-4">
                {DELIVERABLES_P9.map((del) => (
                  <div key={del.code} className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold bg-purple-700 text-white px-2 py-0.5 rounded">
                          {del.code}
                        </span>
                        <h3 className="font-bold text-sm text-[#1A1A1A]">{del.titleEn}</h3>
                      </div>
                      <span className="text-[10px] font-bold bg-white px-2 py-0.5 rounded border border-[#E5E7EB] text-purple-800">
                        {del.category}
                      </span>
                    </div>

                    <p className="text-xs text-[#4B5563] font-medium">{del.summary}</p>

                    <div className="p-3 bg-white rounded-lg border border-[#E5E7EB] space-y-1.5">
                      <div className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider">Key Functional Requirements</div>
                      <ul className="space-y-1 text-xs text-[#4B5563]">
                        {del.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-purple-700 font-bold">•</span>
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="text-[11px] font-mono text-[#6B7280] bg-[#F3F4F6] p-2 rounded border border-[#E5E7EB]">
                      <span className="font-bold text-[#1A1A1A]">Architecture Note:</span> {del.architectureNotes}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* PROMPT 10 SPECIFIC DELIVERABLES VIEW */}
          {selectedSection === ('monetization-p10' as any) && (
            <div className="space-y-6 animate-in fade-in duration-150">
              <div className="border-b border-emerald-200 pb-4 space-y-2">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded bg-emerald-100 text-emerald-900 text-xs font-bold">
                  <Zap className="w-3.5 h-3.5 text-emerald-700" />
                  PROMPT 10 CERTIFIED SPECIFICATIONS
                </div>
                <h2 className="text-2xl font-extrabold text-[#1A1A1A]">
                  YamooPay Global Monetization & Revenue Intelligence Engine
                </h2>
                <p className="text-xs text-[#6B7280]">
                  6 Certified Deliverables: Commercial Governance & Ranking Firewall, AdSlot Abstraction, Protected Calculation Zones, TCF/GPP Privacy Signals, ads.txt Security, and Monetization Command Center.
                </p>
              </div>

              <div className="space-y-4">
                {DELIVERABLES_P10.map((del) => (
                  <div key={del.code} className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold bg-emerald-700 text-white px-2 py-0.5 rounded">
                          {del.code}
                        </span>
                        <h3 className="font-bold text-sm text-[#1A1A1A]">{del.titleEn}</h3>
                      </div>
                      <span className="text-[10px] font-bold bg-white px-2 py-0.5 rounded border border-[#E5E7EB] text-emerald-800">
                        {del.category}
                      </span>
                    </div>

                    <p className="text-xs text-[#4B5563] font-medium">{del.summary}</p>

                    <div className="p-3 bg-white rounded-lg border border-[#E5E7EB] space-y-1.5">
                      <div className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider">Key Functional Requirements</div>
                      <ul className="space-y-1 text-xs text-[#4B5563]">
                        {del.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-emerald-700 font-bold">•</span>
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="text-[11px] font-mono text-[#6B7280] bg-[#F3F4F6] p-2 rounded border border-[#E5E7EB]">
                      <span className="font-bold text-[#1A1A1A]">Architecture Note:</span> {del.architectureNotes}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* PROMPT 11 DELIVERABLES */}
          {selectedSection === ('analytics-p11' as any) && (
            <div className="space-y-6 animate-in fade-in duration-150">
              <div className="border-b border-blue-200 pb-4 space-y-2">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded bg-blue-100 text-blue-900 text-xs font-bold">
                  <Zap className="w-3.5 h-3.5 text-blue-700" />
                  PROMPT 11 CERTIFIED SPECIFICATIONS
                </div>
                <h2 className="text-2xl font-extrabold text-[#1A1A1A]">
                  YamooPay Global Product Analytics & Business Intelligence Engine
                </h2>
                <p className="text-xs text-[#6B7280]">
                  6 Certified Deliverables: Typed Event Taxonomy & Financial Data Privacy Governance, First-Party Collector & GA4 Consent Router, Funnel & Cohort Retention Intelligence, Attribution & Experimentation Engine, Market Opportunity Analytics, and Executive Command Center.
                </p>
              </div>

              <div className="space-y-4">
                {DELIVERABLES_P11.map((del) => (
                  <div key={del.code} className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold bg-blue-700 text-white px-2 py-0.5 rounded">
                          {del.code}
                        </span>
                        <h3 className="font-bold text-sm text-[#1A1A1A]">{del.titleEn}</h3>
                      </div>
                      <span className="text-[10px] font-bold bg-white px-2 py-0.5 rounded border border-[#E5E7EB] text-blue-800">
                        {del.category}
                      </span>
                    </div>

                    <p className="text-xs text-[#4B5563] font-medium">{del.summary}</p>

                    <div className="p-3 bg-white rounded-lg border border-[#E5E7EB] space-y-1.5">
                      <div className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider">Key Functional Requirements</div>
                      <ul className="space-y-1 text-xs text-[#4B5563]">
                        {del.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-blue-700 font-bold">•</span>
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="text-[11px] font-mono text-[#6B7280] bg-[#F3F4F6] p-2 rounded border border-[#E5E7EB]">
                      <span className="font-bold text-[#1A1A1A]">Architecture Note:</span> {del.architectureNotes}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* FALLBACK FOR OTHER SECTIONS */}
          {selectedSection !== ('ux-spec-p2' as any) &&
            selectedSection !== ('tokens-p2' as any) &&
            selectedSection !== 'data-model' &&
            selectedSection !== 'markets' &&
            selectedSection !== 'salary-engine-p6' &&
            selectedSection !== 'loan-engine-p7' &&
            selectedSection !== 'identity-security-p8' &&
            selectedSection !== ('seo-growth-p9' as any) &&
            selectedSection !== ('monetization-p10' as any) &&
            selectedSection !== ('analytics-p11' as any) && (
              <div className="space-y-4 animate-in fade-in duration-150">
                <div className="border-b border-[#E5E7EB] pb-4 space-y-1">
                  <div className="text-xs font-bold text-[#6B7280] uppercase tracking-wider">
                    Specification Section
                  </div>
                  <h2 className="text-2xl font-extrabold text-[#1A1A1A]">
                    {SECTIONS_META.find((s) => s.id === selectedSection)?.titleEn}
                  </h2>
                  <p className="text-xs text-[#6B7280]">
                    {SECTIONS_META.find((s) => s.id === selectedSection)?.summary}
                  </p>
                </div>

                <div className="p-6 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] text-xs text-[#4B5563] leading-relaxed space-y-3">
                  <p>
                    This technical specification is fully certified for <strong>YamooPay 2026 Release</strong>. It defines the formal boundaries, system invariants, and implementation protocols required to maintain institutional-grade financial accuracy and universal multi-currency reliability.
                  </p>
                  <div className="p-3 bg-white rounded-lg border border-[#E5E7EB] font-mono text-[11px] text-[#1A1A1A]">
                    STATUS: CERTIFIED • STRICT LIGHT MODE ONLY • 100% CLIENT-SIDE VERIFIED
                  </div>
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};
